/**
 * EditableOverlayLayer Component
 *
 * Transparent layer that sits on top of RemotionOverlayRenderer.
 * Provides hit targets for each overlay and handles selection, drag, resize.
 * Based on the working prototype in remotion-prototype/editor/CanvasEditingLayer.tsx
 *
 * Key behaviors:
 * - For text overlays: resizing proportionally scales font size (like Fabric.js)
 * - For image/SVG overlays: resizing changes dimensions
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Copy, Crop, Trash2 } from "lucide-react";
import { AnyOverlay, ImageOverlay, TextOverlay } from "../../models/Post";
import { isTextOverlay, isSvgOverlay, isImageOverlay } from "../../lib/utils";
import {
  OverlayHitEntry,
  pickCycleTarget,
  sortOverlayHitStack,
} from "../../utils/overlayClickCycle";
import { clampRectToCanvas } from "../../utils/overlaySelectionIndicator";
import { getOverlayToolbarPosition } from "../../utils/overlayToolbarPosition";
import { isCroppableImage, pickCropTarget } from "../../utils/overlayCropTarget";

// Default padding values (matching RemotionTextOverlay)
const DEFAULT_VERTICAL_PADDING = 8;
const DEFAULT_HORIZONTAL_PADDING = 12;
const SVG_FILTER_HORIZ_PADDING = 8;
const SVG_FILTER_VERT_PADDING = 4;

/** Compute height percentage for an SVG overlay from its viewBox aspect ratio */
function getSvgHeightPct(overlay: AnyOverlay, containerWidth: number, containerHeight: number): number | undefined {
  if (!isSvgOverlay(overlay) || overlay.height !== undefined) return undefined;
  const parts = overlay.viewBox.trim().split(/\s+/).map(Number);
  const vbW = parts[2] || 100;
  const vbH = parts[3] || 100;
  const widthPx = ((overlay.width ?? 50) / 100) * containerWidth;
  const heightPx = widthPx * (vbH / vbW);
  return (heightPx / containerHeight) * 100;
}

type ResizeHandle =
  | "top-left" | "top" | "top-right"
  | "left" | "right"
  | "bottom-left" | "bottom" | "bottom-right";

export interface EditableOverlayLayerProps {
  overlays: AnyOverlay[];
  selectedOverlayId: string | null;
  containerWidth: number;
  containerHeight: number;
  onSelect: (id: string | null) => void;
  onUpdate: (updatedOverlay: AnyOverlay) => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  /** Called when user presses Ctrl/Cmd+C on a selected overlay */
  onCopy?: (overlay: AnyOverlay) => void;
  /** Called when user presses Ctrl/Cmd+V to paste a copied overlay */
  onPaste?: () => void;
  /** Called once when a drag/resize/rotate interaction begins */
  onInteractionStart?: () => void;
  /** Scale factor (containerWidth / referenceWidth) */
  scale?: number;
  /** Reference media width for scaling calculations */
  referenceWidth?: number;
  /** Image URLs map for resolving filePath -> URL */
  imageUrls?: Record<string, string>;
}

type Interaction =
  | { type: "idle" }
  | {
      type: "drag";
      overlayId: string;
      pointerId: number;
      startClientX: number;
      startClientY: number;
      startCanvasX: number;
      startCanvasY: number;
      startOverlayX: number;
      startOverlayY: number;
      hasMoved: boolean;
      /** Press landed on a stack already containing the selection — a clean
       * release cycles selection to the next overlay down the stack. */
      cycleOnRelease: boolean;
    }
  | {
      type: "resize";
      overlayId: string;
      pointerId: number;
      handle: ResizeHandle;
      startClientX: number;
      startClientY: number;
      startCanvasX: number;
      startCanvasY: number;
      startWidth: number;
      startHeight: number;
      startX: number;
      startY: number;
      startFontSize: number;
      hasMoved: boolean;
    }
  | {
      type: "rotate";
      overlayId: string;
      pointerId: number;
      startClientX: number;
      startClientY: number;
      startAngle: number;
      startRotation: number;
      centerX: number;
      centerY: number;
      hasMoved: boolean;
    }
  | {
      type: "crop";
      overlayId: string;
      pointerId: number;
      startClientX: number;
      startClientY: number;
      startObjectPositionX: number;
      startObjectPositionY: number;
      overflowX: number;
      overflowY: number;
      hasMoved: boolean;
    };

interface ImageNaturalSize {
  width: number;
  height: number;
}

interface AlignmentGuides {
  vertical: number[];
  horizontal: number[];
}

const SNAP_THRESHOLD_PCT = 1.2;
const POINTER_MOVE_THRESHOLD_PX = 4;

const capturePointer = (target: Element, pointerId: number) => {
  try {
    target.setPointerCapture(pointerId);
  } catch {
    // The pointer may already be captured or released by the browser.
  }
};

const getCoverOverflow = (
  frameWidth: number,
  frameHeight: number,
  naturalSize?: ImageNaturalSize | null
) => {
  if (!naturalSize?.width || !naturalSize?.height) {
    return {
      overflowX: Math.max(1, frameWidth),
      overflowY: Math.max(1, frameHeight),
    };
  }

  const imageScale = Math.max(frameWidth / naturalSize.width, frameHeight / naturalSize.height);
  const renderedWidth = naturalSize.width * imageScale;
  const renderedHeight = naturalSize.height * imageScale;

  return {
    overflowX: Math.max(0, renderedWidth - frameWidth),
    overflowY: Math.max(0, renderedHeight - frameHeight),
  };
};

const getDirectImageUrl = (filePath: string) => {
  return /^(https?:|blob:|data:)/.test(filePath) ? filePath : undefined;
};

export const EditableOverlayLayer: React.FC<EditableOverlayLayerProps> = ({
  overlays,
  selectedOverlayId,
  containerWidth,
  containerHeight,
  onSelect,
  onUpdate,
  onDelete,
  onDuplicate,
  onCopy,
  onPaste,
  onInteractionStart,
  scale = 1,
  referenceWidth,
  imageUrls = {},
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [interaction, setInteractionState] = useState<Interaction>({ type: "idle" });
  const interactionRef = useRef<Interaction>({ type: "idle" });
  const [cropModeOverlayId, setCropModeOverlayId] = useState<string | null>(null);
  const [alignmentGuides, setAlignmentGuides] = useState<AlignmentGuides>({
    vertical: [],
    horizontal: [],
  });
  const shiftKeyRef = useRef(false);

  const selectedOverlay = overlays.find((o) => o.id === selectedOverlayId);

  const setInteraction = useCallback((next: Interaction | ((prev: Interaction) => Interaction)) => {
    const resolved = typeof next === "function"
      ? (next as (prev: Interaction) => Interaction)(interactionRef.current)
      : next;
    interactionRef.current = resolved;
    setInteractionState(resolved);
  }, []);

  useEffect(() => {
    if (!cropModeOverlayId) return;

    const cropModeOverlay = overlays.find((overlay) => overlay.id === cropModeOverlayId);
    if (
      cropModeOverlayId !== selectedOverlayId ||
      !cropModeOverlay ||
      !isImageOverlay(cropModeOverlay) ||
      (cropModeOverlay.objectFit ?? "cover") !== "cover"
    ) {
      setCropModeOverlayId(null);
    }
  }, [cropModeOverlayId, overlays, selectedOverlayId]);

  // Calculate scale factor
  const scaleFactor = referenceWidth && referenceWidth > 0
    ? containerWidth / referenceWidth
    : 1;

  // Convert pixels to percentage
  const pxToPct = useCallback(
    (px: number, dimension: "x" | "y") => {
      return dimension === "x"
        ? (px / containerWidth) * 100
        : (px / containerHeight) * 100;
    },
    [containerWidth, containerHeight]
  );

  const updateAlignmentGuides = useCallback(
    (verticalGuide: number | null, horizontalGuide: number | null) => {
      const nextVertical = verticalGuide === null ? [] : [verticalGuide];
      const nextHorizontal = horizontalGuide === null ? [] : [horizontalGuide];
      setAlignmentGuides((prev) => {
        const isSameVertical =
          prev.vertical.length === nextVertical.length &&
          prev.vertical[0] === nextVertical[0];
        const isSameHorizontal =
          prev.horizontal.length === nextHorizontal.length &&
          prev.horizontal[0] === nextHorizontal[0];
        return isSameVertical && isSameHorizontal
          ? prev
          : { vertical: nextVertical, horizontal: nextHorizontal };
      });
    },
    []
  );

  const handleBackgroundPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        updateAlignmentGuides(null, null);
        setInteraction({ type: "idle" });
        setCropModeOverlayId(null);
        onSelect(null);
      }
    },
    [onSelect, setInteraction, updateAlignmentGuides]
  );

  const getOverlayDimensionsPct = useCallback(
    (overlay: AnyOverlay) => {
      let widthPct = overlay.width ?? 50;
      let heightPct = overlay.height
        ?? getSvgHeightPct(overlay, containerWidth, containerHeight)
        ?? 10;

      if (containerRef.current && ((overlay.height === undefined && !isSvgOverlay(overlay)) || overlay.width === undefined)) {
        const overlayElement = containerRef.current.querySelector(
          `[data-overlay-id="${overlay.id}"]`
        ) as HTMLElement | null;

        if (overlayElement) {
          const measuredWidthPct = pxToPct(overlayElement.offsetWidth, "x");
          const measuredHeightPct = pxToPct(overlayElement.offsetHeight, "y");
          if (overlay.width === undefined && measuredWidthPct > 0) {
            widthPct = measuredWidthPct;
          }
          if (overlay.height === undefined && !isSvgOverlay(overlay) && measuredHeightPct > 0) {
            heightPct = measuredHeightPct;
          }
        }
      }

      return {
        width: widthPct,
        height: heightPct,
      };
    },
    [containerHeight, containerWidth, pxToPct]
  );

  const findOverlayStackAtPoint = useCallback(
    (clickXPct: number, clickYPct: number): AnyOverlay[] => {
      const entries: OverlayHitEntry<AnyOverlay>[] = [];

      overlays.forEach((candidate, index) => {
        const { width, height } = getOverlayDimensionsPct(candidate);
        const left = candidate.position.x;
        const top = candidate.position.y;
        const isInside =
          clickXPct >= left &&
          clickXPct <= left + width &&
          clickYPct >= top &&
          clickYPct <= top + height;

        if (!isInside) return;

        entries.push({
          overlay: candidate,
          zIndex: candidate.zIndex ?? 0,
          area: width * height,
          index,
        });
      });

      return sortOverlayHitStack(entries);
    },
    [getOverlayDimensionsPct, overlays]
  );

  const getCanvasPoint = useCallback((clientX: number, clientY: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }, []);

  const enterCropMode = useCallback(
    (overlay: ImageOverlay) => {
      onSelect(overlay.id);
      setInteraction({ type: "idle" });
      setCropModeOverlayId(overlay.id);
      updateAlignmentGuides(null, null);
    },
    [onSelect, setInteraction, updateAlignmentGuides]
  );

  const handleOverlayPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, overlay: AnyOverlay) => {
      if (e.button !== 0) return;

      e.preventDefault();
      e.stopPropagation();
      capturePointer(e.currentTarget, e.pointerId);

      const point = getCanvasPoint(e.clientX, e.clientY);
      if (!point) return;

      const stack = findOverlayStackAtPoint(
        (point.x / containerWidth) * 100,
        (point.y / containerHeight) * 100
      );
      // When the press lands on a stack that already contains the selection,
      // keep it selected: drags must move the selected overlay even while
      // occluded, and a clean release cycles selection down the stack —
      // re-selecting the topmost here would make layers below the second
      // one unreachable.
      const selectedInStack = selectedOverlayId
        ? stack.find((o) => o.id === selectedOverlayId)
        : undefined;
      const targetOverlay = selectedInStack ?? stack[0] ?? overlay;

      if (!selectedInStack) {
        onSelect(targetOverlay.id);
      }
      if (cropModeOverlayId && cropModeOverlayId !== targetOverlay.id) {
        setCropModeOverlayId(null);
      }

      setInteraction({
        type: "drag",
        overlayId: targetOverlay.id,
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startCanvasX: point.x,
        startCanvasY: point.y,
        startOverlayX: targetOverlay.position.x,
        startOverlayY: targetOverlay.position.y,
        hasMoved: false,
        cycleOnRelease: Boolean(selectedInStack) && stack.length > 1,
      });
    },
    [
      containerHeight,
      containerWidth,
      cropModeOverlayId,
      findOverlayStackAtPoint,
      getCanvasPoint,
      onSelect,
      selectedOverlayId,
      setInteraction,
    ]
  );

  const handleOverlayDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, overlay: AnyOverlay) => {
      e.preventDefault();
      e.stopPropagation();

      const point = getCanvasPoint(e.clientX, e.clientY);
      const stack = point
        ? findOverlayStackAtPoint(
            (point.x / containerWidth) * 100,
            (point.y / containerHeight) * 100
          )
        : [overlay];

      const target = pickCropTarget(stack, selectedOverlayId);
      if (target) {
        enterCropMode(target);
      }
    },
    [
      containerHeight,
      containerWidth,
      enterCropMode,
      findOverlayStackAtPoint,
      getCanvasPoint,
      selectedOverlayId,
    ]
  );

  const handleCropPointerDown = useCallback(
    (
      e: React.PointerEvent<HTMLDivElement>,
      overlay: ImageOverlay,
      elementRef: React.RefObject<HTMLDivElement | null>,
      naturalSize?: ImageNaturalSize | null
    ) => {
      if (e.button !== 0) return;

      e.preventDefault();
      e.stopPropagation();
      capturePointer(e.currentTarget, e.pointerId);

      const rect = elementRef.current?.getBoundingClientRect();
      if (!rect) return;

      const { overflowX, overflowY } = getCoverOverflow(rect.width, rect.height, naturalSize);

      onSelect(overlay.id);
      setInteraction({
        type: "crop",
        overlayId: overlay.id,
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startObjectPositionX: overlay.objectPosition?.x ?? 50,
        startObjectPositionY: overlay.objectPosition?.y ?? 50,
        overflowX,
        overflowY,
        hasMoved: false,
      });
    },
    [onSelect, setInteraction]
  );

  const handleResizeStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, overlay: AnyOverlay, handle: ResizeHandle, measuredHeightPx: number) => {
      if (e.button !== 0) return;

      e.preventDefault();
      e.stopPropagation();
      capturePointer(e.currentTarget, e.pointerId);

      const point = getCanvasPoint(e.clientX, e.clientY);
      if (!point) return;

      const heightPct = overlay.height !== undefined
        ? overlay.height
        : pxToPct(measuredHeightPx, "y");
      const currentFontSize = isTextOverlay(overlay)
        ? overlay.style.fontSize
        : 0;

      onSelect(overlay.id);
      setCropModeOverlayId(null);
      setInteraction({
        type: "resize",
        overlayId: overlay.id,
        pointerId: e.pointerId,
        handle,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startCanvasX: point.x,
        startCanvasY: point.y,
        startWidth: overlay.width ?? 50,
        startHeight: heightPct,
        startX: overlay.position.x,
        startY: overlay.position.y,
        startFontSize: currentFontSize,
        hasMoved: false,
      });
    },
    [getCanvasPoint, onSelect, pxToPct, setInteraction]
  );

  const handleRotationStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, overlay: AnyOverlay, elementRef: React.RefObject<HTMLDivElement | null>) => {
      if (e.button !== 0) return;

      e.preventDefault();
      e.stopPropagation();
      capturePointer(e.currentTarget, e.pointerId);

      if (!elementRef.current) return;

      const rect = elementRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const deltaX = e.clientX - centerX;
      const deltaY = e.clientY - centerY;
      const startAngle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
      const currentRotation = isTextOverlay(overlay)
        ? overlay.rotation ?? 0
        : (overlay as any).rotation ?? 0;

      onSelect(overlay.id);
      setCropModeOverlayId(null);
      setInteraction({
        type: "rotate",
        overlayId: overlay.id,
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startAngle,
        startRotation: currentRotation,
        centerX,
        centerY,
        hasMoved: false,
      });
    },
    [onSelect, setInteraction]
  );

  const getAlignmentTargets = useCallback(
    (movingOverlayId: string) => {
      const verticalTargets = new Set<number>([0, 50, 100]);
      const horizontalTargets = new Set<number>([0, 50, 100]);

      overlays.forEach((overlay) => {
        if (overlay.id === movingOverlayId) return;

        const { width, height } = getOverlayDimensionsPct(overlay);
        const left = overlay.position.x;
        const top = overlay.position.y;
        const right = left + width;
        const bottom = top + height;
        const centerX = left + width / 2;
        const centerY = top + height / 2;

        verticalTargets.add(left);
        verticalTargets.add(centerX);
        verticalTargets.add(right);
        horizontalTargets.add(top);
        horizontalTargets.add(centerY);
        horizontalTargets.add(bottom);
      });

      return {
        verticalTargets: Array.from(verticalTargets),
        horizontalTargets: Array.from(horizontalTargets),
      };
    },
    [overlays, getOverlayDimensionsPct]
  );

  const getSnappedPosition = useCallback(
    (rawPositionPct: number, overlaySizePct: number, targets: number[]) => {
      const anchors = [
        { value: rawPositionPct, offset: 0 },
        { value: rawPositionPct + overlaySizePct / 2, offset: overlaySizePct / 2 },
        { value: rawPositionPct + overlaySizePct, offset: overlaySizePct },
      ];

      let bestMatch: { snappedPositionPct: number; guidePct: number; distance: number } | null = null;

      for (const anchor of anchors) {
        for (const target of targets) {
          const distance = Math.abs(anchor.value - target);
          if (distance > SNAP_THRESHOLD_PCT) {
            continue;
          }

          if (!bestMatch || distance < bestMatch.distance) {
            bestMatch = {
              snappedPositionPct: target - anchor.offset,
              guidePct: target,
              distance,
            };
          }
        }
      }

      if (!bestMatch) {
        return { positionPct: rawPositionPct, guidePct: null as number | null };
      }

      return { positionPct: bestMatch.snappedPositionPct, guidePct: bestMatch.guidePct };
    },
    []
  );

  const getNearestTarget = useCallback((value: number, targets: number[]) => {
    let bestTarget: number | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const target of targets) {
      const distance = Math.abs(value - target);
      if (distance > SNAP_THRESHOLD_PCT) {
        continue;
      }
      if (distance < bestDistance) {
        bestDistance = distance;
        bestTarget = target;
      }
    }

    return bestTarget;
  }, []);

  const getStartedInteraction = useCallback(
    function <T extends Exclude<Interaction, { type: "idle" }>>(active: T, e: PointerEvent): T | null {
      if (active.hasMoved) return active;

      const distance = Math.hypot(e.clientX - active.startClientX, e.clientY - active.startClientY);
      if (distance < POINTER_MOVE_THRESHOLD_PX) return null;

      const next = { ...active, hasMoved: true } as T;
      onInteractionStart?.();
      setInteraction(next);
      return next;
    },
    [onInteractionStart, setInteraction]
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const active = interactionRef.current;
      if (active.type === "idle" || e.pointerId !== active.pointerId) return;

      const started = getStartedInteraction(active, e);
      if (!started) return;
      const activeInteraction = started;

      e.preventDefault();

      const activeOverlay = overlays.find((overlay) => overlay.id === activeInteraction.overlayId);
      if (!activeOverlay) {
        setInteraction({ type: "idle" });
        updateAlignmentGuides(null, null);
        return;
      }

      if (activeInteraction.type === "rotate") {
        updateAlignmentGuides(null, null);
        const deltaX = e.clientX - activeInteraction.centerX;
        const deltaY = e.clientY - activeInteraction.centerY;
        const currentAngle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
        const deltaAngle = currentAngle - activeInteraction.startAngle;

        let newRotation = activeInteraction.startRotation + deltaAngle;
        if (shiftKeyRef.current) {
          newRotation = Math.round(newRotation / 15) * 15;
        }

        while (newRotation < 0) newRotation += 360;
        while (newRotation >= 360) newRotation -= 360;

        onUpdate({
          ...activeOverlay,
          rotation: newRotation,
        } as AnyOverlay);
        return;
      }

      if (activeInteraction.type === "crop") {
        if (!isImageOverlay(activeOverlay)) return;

        updateAlignmentGuides(null, null);
        const deltaX = e.clientX - activeInteraction.startClientX;
        const deltaY = e.clientY - activeInteraction.startClientY;
        const clampPosition = (value: number) => Math.min(100, Math.max(0, Math.round(value * 10) / 10));
        const nextX = activeInteraction.overflowX > 0
          ? clampPosition(activeInteraction.startObjectPositionX - (deltaX / activeInteraction.overflowX) * 100)
          : activeInteraction.startObjectPositionX;
        const nextY = activeInteraction.overflowY > 0
          ? clampPosition(activeInteraction.startObjectPositionY - (deltaY / activeInteraction.overflowY) * 100)
          : activeInteraction.startObjectPositionY;

        onUpdate({
          ...activeOverlay,
          objectPosition: {
            x: nextX,
            y: nextY,
          },
        });
        return;
      }

      const point = getCanvasPoint(e.clientX, e.clientY);
      if (!point) return;

      if (activeInteraction.type === "drag") {
        const deltaXPx = point.x - activeInteraction.startCanvasX;
        const deltaYPx = point.y - activeInteraction.startCanvasY;
        const rawX = activeInteraction.startOverlayX + pxToPct(deltaXPx, "x");
        const rawY = activeInteraction.startOverlayY + pxToPct(deltaYPx, "y");
        const { width, height } = getOverlayDimensionsPct(activeOverlay);
        const { verticalTargets, horizontalTargets } = getAlignmentTargets(activeOverlay.id);
        const snappedX = getSnappedPosition(rawX, width, verticalTargets);
        const snappedY = getSnappedPosition(rawY, height, horizontalTargets);

        updateAlignmentGuides(snappedX.guidePct, snappedY.guidePct);
        onUpdate({
          ...activeOverlay,
          position: { x: snappedX.positionPct, y: snappedY.positionPct },
        });
        return;
      }

      if (activeInteraction.type === "resize") {
        const deltaXPx = point.x - activeInteraction.startCanvasX;
        const deltaYPx = point.y - activeInteraction.startCanvasY;
        const deltaXPct = pxToPct(deltaXPx, "x");
        const deltaYPct = pxToPct(deltaYPx, "y");

        let newWidth = activeInteraction.startWidth;
        let newHeight = activeInteraction.startHeight;
        let newX = activeInteraction.startX;
        let newY = activeInteraction.startY;

        const handle = activeInteraction.handle;
        const isCorner = handle.includes("-");
        const maintainAspectRatio = isCorner && !isTextOverlay(activeOverlay);

        if (maintainAspectRatio) {
          const startWidthPx = activeInteraction.startWidth * containerWidth / 100;
          const startHeightPx = activeInteraction.startHeight * containerHeight / 100;
          const signX = handle.includes("right") ? 1 : -1;
          const signY = handle.includes("bottom") ? 1 : -1;
          const diagX = signX * startWidthPx;
          const diagY = signY * startHeightPx;
          const diagLenSq = diagX * diagX + diagY * diagY;
          const dot = deltaXPx * diagX + deltaYPx * diagY;
          const resizeScale = dot / diagLenSq;

          newWidth = activeInteraction.startWidth * (1 + resizeScale);
          newHeight = activeInteraction.startHeight * (1 + resizeScale);

          if (handle.includes("left")) {
            newX = activeInteraction.startX + (activeInteraction.startWidth - newWidth);
          }
          if (handle.includes("top")) {
            newY = activeInteraction.startY + (activeInteraction.startHeight - newHeight);
          }
        } else {
          if (handle.includes("right")) {
            newWidth = activeInteraction.startWidth + deltaXPct;
          } else if (handle.includes("left")) {
            newWidth = activeInteraction.startWidth - deltaXPct;
            newX = activeInteraction.startX + deltaXPct;
          }

          if (handle.includes("bottom")) {
            newHeight = activeInteraction.startHeight + deltaYPct;
          } else if (handle.includes("top")) {
            newHeight = activeInteraction.startHeight - deltaYPct;
            newY = activeInteraction.startY + deltaYPct;
          }
        }

        newWidth = Math.max(0.5, newWidth);
        newHeight = Math.max(0.5, newHeight);

        const aspectRatio = maintainAspectRatio
          ? activeInteraction.startWidth / activeInteraction.startHeight
          : null;

        const right = newX + newWidth;
        const bottom = newY + newHeight;
        const { verticalTargets, horizontalTargets } = getAlignmentTargets(activeOverlay.id);
        let verticalGuide: number | null = null;
        let horizontalGuide: number | null = null;

        if (handle.includes("right")) {
          const snappedRight = getNearestTarget(right, verticalTargets);
          if (snappedRight !== null) {
            newWidth = snappedRight - newX;
            if (aspectRatio) newHeight = newWidth / aspectRatio;
            verticalGuide = snappedRight;
          }
        } else if (handle.includes("left")) {
          const snappedLeft = getNearestTarget(newX, verticalTargets);
          if (snappedLeft !== null) {
            newWidth = right - snappedLeft;
            newX = snappedLeft;
            if (aspectRatio) {
              const oldHeight = newHeight;
              newHeight = newWidth / aspectRatio;
              if (handle.includes("top")) {
                newY = newY + (oldHeight - newHeight);
              }
            }
            verticalGuide = snappedLeft;
          }
        }

        if (!aspectRatio) {
          if (handle.includes("bottom")) {
            const snappedBottom = getNearestTarget(bottom, horizontalTargets);
            if (snappedBottom !== null) {
              newHeight = snappedBottom - newY;
              horizontalGuide = snappedBottom;
            }
          } else if (handle.includes("top")) {
            const snappedTop = getNearestTarget(newY, horizontalTargets);
            if (snappedTop !== null) {
              newHeight = bottom - snappedTop;
              newY = snappedTop;
              horizontalGuide = snappedTop;
            }
          }
        }

        if (verticalGuide === null && (handle.includes("right") || handle.includes("left"))) {
          const centerX = newX + newWidth / 2;
          const snappedCenterX = getNearestTarget(centerX, verticalTargets);
          if (snappedCenterX !== null) {
            const delta = snappedCenterX - centerX;
            if (handle.includes("right")) {
              newWidth = newWidth + delta * 2;
            } else {
              newWidth = newWidth + delta * 2;
              newX = newX - delta;
            }
            if (aspectRatio) {
              const oldHeight = newHeight;
              newHeight = newWidth / aspectRatio;
              if (handle.includes("top")) {
                newY = newY + (oldHeight - newHeight);
              }
            }
            verticalGuide = snappedCenterX;
          }
        }

        if (horizontalGuide === null && !aspectRatio) {
          if (handle.includes("bottom") || handle.includes("top")) {
            const centerY = newY + newHeight / 2;
            const snappedCenterY = getNearestTarget(centerY, horizontalTargets);
            if (snappedCenterY !== null) {
              const delta = snappedCenterY - centerY;
              if (handle.includes("bottom")) {
                newHeight = newHeight + delta * 2;
              } else {
                newHeight = newHeight + delta * 2;
                newY = newY - delta;
              }
              horizontalGuide = snappedCenterY;
            }
          }
        }

        newWidth = Math.max(0.5, newWidth);
        newHeight = Math.max(0.5, newHeight);
        updateAlignmentGuides(verticalGuide, horizontalGuide);

        if (isTextOverlay(activeOverlay)) {
          const scaleY = newHeight / activeInteraction.startHeight;
          const newFontSize = Math.max(
            1,
            Math.round(activeInteraction.startFontSize * scaleY)
          );

          onUpdate({
            ...activeOverlay,
            width: newWidth,
            position: { x: newX, y: newY },
            style: {
              ...activeOverlay.style,
              fontSize: newFontSize,
            },
          } as TextOverlay);
        } else {
          onUpdate({
            ...activeOverlay,
            width: newWidth,
            height: newHeight,
            position: { x: newX, y: newY },
          });
        }
      }
    },
    [
      containerHeight,
      containerWidth,
      getAlignmentTargets,
      getCanvasPoint,
      getNearestTarget,
      getOverlayDimensionsPct,
      getSnappedPosition,
      getStartedInteraction,
      onUpdate,
      overlays,
      pxToPct,
      setInteraction,
      updateAlignmentGuides,
    ]
  );

  const handlePointerEnd = useCallback(
    (e: PointerEvent) => {
      const active = interactionRef.current;
      if (active.type === "idle" || e.pointerId !== active.pointerId) return;

      if (
        active.type === "drag" &&
        active.cycleOnRelease &&
        !active.hasMoved &&
        // pointercancel is an interrupted gesture (OS swipe, palm rejection),
        // not a click — never cycle on it.
        e.type !== "pointercancel" &&
        // Selection changed mid-hold (async edit, keyboard) — the press-time
        // stack position is stale, don't override the newer selection.
        active.overlayId === selectedOverlayId &&
        !cropModeOverlayId
      ) {
        const stackIds = findOverlayStackAtPoint(
          (active.startCanvasX / containerWidth) * 100,
          (active.startCanvasY / containerHeight) * 100
        ).map((o) => o.id);
        const next = pickCycleTarget(stackIds, active.overlayId);
        if (next && next !== active.overlayId) {
          onSelect(next);
        }
      }

      updateAlignmentGuides(null, null);
      setInteraction({ type: "idle" });
    },
    [
      containerHeight,
      containerWidth,
      cropModeOverlayId,
      findOverlayStackAtPoint,
      onSelect,
      selectedOverlayId,
      setInteraction,
      updateAlignmentGuides,
    ]
  );

  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Don't handle if focus is on an input
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        return;
      }

      if (e.key === "Escape" && interactionRef.current.type !== "idle") {
        e.preventDefault();
        updateAlignmentGuides(null, null);
        setInteraction({ type: "idle" });
        return;
      }

      const isModKey = e.metaKey || e.ctrlKey;

      // Ctrl/Cmd+V paste works even without selection (paste onto current slide)
      if (isModKey && e.key === "v") {
        if (onPaste) {
          e.preventDefault();
          onPaste();
        }
        return;
      }

      if (!selectedOverlayId || !selectedOverlay) return;

      // Ctrl/Cmd+C copies the selected overlay to clipboard
      if (isModKey && e.key === "c") {
        if (onCopy) {
          e.preventDefault();
          onCopy(selectedOverlay);
        }
        return;
      }

      const moveAmount = e.shiftKey ? 10 : 1;

      switch (e.key) {
        case "Delete":
        case "Backspace":
          if (onDelete) {
            e.preventDefault();
            onDelete(selectedOverlayId);
          }
          break;
        case "ArrowLeft":
          e.preventDefault();
          onUpdate({
            ...selectedOverlay,
            position: {
              x: selectedOverlay.position.x - moveAmount,
              y: selectedOverlay.position.y,
            },
          });
          break;
        case "ArrowRight":
          e.preventDefault();
          onUpdate({
            ...selectedOverlay,
            position: {
              x: selectedOverlay.position.x + moveAmount,
              y: selectedOverlay.position.y,
            },
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          onUpdate({
            ...selectedOverlay,
            position: {
              x: selectedOverlay.position.x,
              y: selectedOverlay.position.y - moveAmount,
            },
          });
          break;
        case "ArrowDown":
          e.preventDefault();
          onUpdate({
            ...selectedOverlay,
            position: {
              x: selectedOverlay.position.x,
              y: selectedOverlay.position.y + moveAmount,
            },
          });
          break;
        case "Escape":
          if (cropModeOverlayId) {
            e.preventDefault();
            setCropModeOverlayId(null);
            break;
          }
          onSelect(null);
          break;
      }
    },
    [selectedOverlayId, selectedOverlay, onUpdate, onDelete, onSelect, onCopy, onPaste, cropModeOverlayId, setInteraction, updateAlignmentGuides]
  );

  // Track Shift key for rotation snapping
  useEffect(() => {
    const down = (e: KeyboardEvent) => { if (e.key === "Shift") shiftKeyRef.current = true; };
    const up = (e: KeyboardEvent) => { if (e.key === "Shift") shiftKeyRef.current = false; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  // Add global event listeners for drag/resize/rotate/crop
  useEffect(() => {
    if (interaction.type !== "idle") {
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerEnd);
      window.addEventListener("pointercancel", handlePointerEnd);
      return () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerEnd);
        window.removeEventListener("pointercancel", handlePointerEnd);
      };
    }
  }, [interaction.type, handlePointerMove, handlePointerEnd]);

  // Keyboard event listener
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div
      ref={containerRef}
      onPointerDown={handleBackgroundPointerDown}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: containerWidth,
        height: containerHeight,
        cursor: interaction.type === "drag" && interaction.hasMoved
          ? "grabbing"
          : interaction.type === "rotate"
            ? "crosshair"
            : "default",
        userSelect: "none",
        touchAction: "none",
      }}
    >
      {alignmentGuides.vertical.map((guidePct) => (
        <div
          key={`guide-v-${guidePct}`}
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `calc(${guidePct}% - 0.5px)`,
            width: 1,
            backgroundColor: "rgba(255, 92, 53, 0.8)",
            pointerEvents: "none",
            zIndex: 9999,
          }}
        />
      ))}
      {alignmentGuides.horizontal.map((guidePct) => (
        <div
          key={`guide-h-${guidePct}`}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: `calc(${guidePct}% - 0.5px)`,
            height: 1,
            backgroundColor: "rgba(255, 92, 53, 0.8)",
            pointerEvents: "none",
            zIndex: 9999,
          }}
        />
      ))}

      {/* Render hit targets for each overlay */}
      {overlays.map((overlay) => (
        <OverlayHitTarget
          key={overlay.id}
          overlay={overlay}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
          isSelected={overlay.id === selectedOverlayId}
          onPointerDown={(e) => handleOverlayPointerDown(e, overlay)}
          onDoubleClick={(e) => handleOverlayDoubleClick(e, overlay)}
          onCropPointerDown={handleCropPointerDown}
          onCropModeChange={(id) => {
            setInteraction({ type: "idle" });
            setCropModeOverlayId(id);
          }}
          onResizeStart={handleResizeStart}
          onRotationStart={handleRotationStart}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
          scale={scaleFactor}
          isCropDragging={interaction.type === "crop" && interaction.overlayId === overlay.id && interaction.hasMoved}
          isCropMode={cropModeOverlayId === overlay.id}
          imageUrl={isImageOverlay(overlay) ? imageUrls[overlay.filePath] ?? getDirectImageUrl(overlay.filePath) : undefined}
        />
      ))}
    </div>
  );
};

// Fabric.js style constants
const BORDER_COLOR = "rgba(255, 92, 53, 0.6)"; // #FF5C3599 semi-transparent orange
const SELECTION_BORDER_WIDTH = 1.5;
const HANDLE_STROKE_COLOR = "#FF5C35";
const HANDLE_FILL_COLOR = "#FFFFFF";
const HANDLE_SIZE = 8;
const ROTATION_LINE_LENGTH = 20;

const useImageNaturalSize = (imageUrl?: string) => {
  const [naturalSize, setNaturalSize] = useState<ImageNaturalSize | null>(null);

  useEffect(() => {
    if (!imageUrl) {
      setNaturalSize(null);
      return;
    }

    let isCancelled = false;
    const image = new Image();
    image.onload = () => {
      if (!isCancelled && image.naturalWidth && image.naturalHeight) {
        setNaturalSize({ width: image.naturalWidth, height: image.naturalHeight });
      }
    };
    image.onerror = () => {
      if (!isCancelled) setNaturalSize(null);
    };
    image.src = imageUrl;

    return () => {
      isCancelled = true;
    };
  }, [imageUrl]);

  return naturalSize;
};

// Hit target for an individual overlay
interface OverlayHitTargetProps {
  overlay: AnyOverlay;
  containerWidth: number;
  containerHeight: number;
  isSelected: boolean;
  onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  onDoubleClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onCropPointerDown: (
    e: React.PointerEvent<HTMLDivElement>,
    overlay: ImageOverlay,
    elementRef: React.RefObject<HTMLDivElement | null>,
    naturalSize?: ImageNaturalSize | null
  ) => void;
  onCropModeChange: (id: string | null) => void;
  onResizeStart: (e: React.PointerEvent<HTMLDivElement>, overlay: AnyOverlay, handle: ResizeHandle, measuredHeightPx: number) => void;
  onRotationStart: (e: React.PointerEvent<HTMLDivElement>, overlay: AnyOverlay, elementRef: React.RefObject<HTMLDivElement | null>) => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  scale: number;
  isCropDragging: boolean;
  isCropMode: boolean;
  imageUrl?: string;
}

const OverlayHitTarget: React.FC<OverlayHitTargetProps> = ({
  overlay,
  containerWidth,
  containerHeight,
  isSelected,
  onPointerDown,
  onDoubleClick,
  onCropPointerDown,
  onCropModeChange,
  onResizeStart,
  onRotationStart,
  onDelete,
  onDuplicate,
  scale,
  isCropDragging,
  isCropMode,
  imageUrl,
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const naturalSize = useImageNaturalSize(imageUrl);

  const left = (overlay.position.x / 100) * containerWidth;
  const top = (overlay.position.y / 100) * containerHeight;
  const width = ((overlay.width ?? 50) / 100) * containerWidth;

  // Get rotation
  const rotation = isTextOverlay(overlay)
    ? overlay.rotation
    : (overlay as any).rotation ?? 0;

  // For explicit height, use it. For SVG overlays, compute from viewBox. Otherwise, let content determine height.
  const svgHeightPct = getSvgHeightPct(overlay, containerWidth, containerHeight);
  const effectiveHeightPct = overlay.height ?? svgHeightPct;
  const hasExplicitHeight = effectiveHeightPct !== undefined;
  const height = hasExplicitHeight
    ? (effectiveHeightPct / 100) * containerHeight
    : undefined;
  // Croppable = cover-fit AND real image content; color swatches render as
  // flat color, so the crop button / pan gesture must never appear for them.
  const isFillImage = isCroppableImage(overlay);
  const shouldPanCrop = isSelected && isFillImage && isCropMode;
  const [toolbarPosition, setToolbarPosition] = useState({ left: 0, top: 0 });
  const toolbarButtonCount = Number(isFillImage) + Number(!!onDuplicate) + Number(!!onDelete);
  const toolbarHeight = 6 + 28 * toolbarButtonCount + 2 * Math.max(0, toolbarButtonCount - 1);

  useLayoutEffect(() => {
    const element = elementRef.current;
    const canvas = element?.parentElement;
    if (!isSelected || !element || !canvas) return;
    const updatePosition = () => {
      const bounds = element.getBoundingClientRect();
      const canvasBounds = canvas.getBoundingClientRect();
      if (!canvasBounds.width || !canvasBounds.height) return;
      const scaleX = containerWidth / canvasBounds.width;
      const scaleY = containerHeight / canvasBounds.height;
      setToolbarPosition(getOverlayToolbarPosition({
        left: (bounds.left - canvasBounds.left) * scaleX,
        top: (bounds.top - canvasBounds.top) * scaleY,
        width: bounds.width * scaleX,
        height: bounds.height * scaleY,
      }, containerWidth, containerHeight, toolbarHeight));
    };
    updatePosition();
    const observer = new ResizeObserver(updatePosition);
    observer.observe(element);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [isSelected, overlay, containerWidth, containerHeight, toolbarHeight]);

  // The image is only adjustable (pannable) once its natural size is known and
  // the cover-scaled image actually overflows its frame in some direction.
  // If it fits exactly there is nothing to reposition, so we hide the hint.
  const coverOverflow =
    isFillImage && height !== undefined && naturalSize
      ? getCoverOverflow(width, height, naturalSize)
      : null;
  const canAdjustImage =
    !!coverOverflow && (coverOverflow.overflowX > 0.5 || coverOverflow.overflowY > 0.5);

  // Handle resize start by measuring actual element height
  const handleResizeStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, handle: ResizeHandle) => {
      const measuredHeight = elementRef.current?.offsetHeight ?? 0;
      onResizeStart(e, overlay, handle, measuredHeight);
    },
    [onResizeStart, overlay]
  );

  // Overlays larger than the canvas (oversized template gradients) have their
  // real selection border clipped away by the canvas overflow — draw a dashed
  // outline on the visible intersection instead so selection is never
  // invisible. Skipped for rotated overlays: the intersection of a rotated
  // rect isn't axis-aligned in this div's local space.
  const visibleSelectionRect =
    isSelected && !rotation && height !== undefined
      ? clampRectToCanvas({ left, top, width, height }, containerWidth, containerHeight)
      : null;

  return (
    <div
      ref={elementRef}
      onPointerDown={(e) => {
        if (shouldPanCrop && isImageOverlay(overlay)) {
          onCropPointerDown(e, overlay, elementRef, naturalSize);
          return;
        }
        onPointerDown(e);
      }}
      onDoubleClick={onDoubleClick}
      style={{
        position: "absolute",
        left,
        top,
        width,
        height: height, // undefined = auto-size to content
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
        transformOrigin: "center",
        cursor: shouldPanCrop ? (isCropDragging ? "grabbing" : "grab") : "grab",
        // Fabric.js style selection border
        border: isSelected
          ? `1.5px ${isCropMode ? "dashed" : "solid"} ${BORDER_COLOR}`
          : "1.5px solid transparent",
        boxSizing: "border-box",
        zIndex: isSelected ? 9998 : (overlay.zIndex ?? 0) + 1000,
      }}
      data-overlay-id={overlay.id}
    >
      {/* Render invisible content to get accurate sizing */}
      {!hasExplicitHeight && (
        <InvisibleContent overlay={overlay} scale={scale} />
      )}

      {/* Controls - only show when selected */}
      {isSelected && (
        <>
          {visibleSelectionRect && (
            <div
              style={{
                position: "absolute",
                // Children are positioned relative to the inside of the
                // parent's selection border — compensate so the outline
                // lands exactly on the canvas intersection instead of
                // being pushed past the canvas edge and clipped.
                left: visibleSelectionRect.left - left - SELECTION_BORDER_WIDTH,
                top: visibleSelectionRect.top - top - SELECTION_BORDER_WIDTH,
                width: visibleSelectionRect.width,
                height: visibleSelectionRect.height,
                // Dash only the edges where the canvas cuts the overlay off —
                // the true edges already show the parent's solid border.
                borderLeft: visibleSelectionRect.clipped.left
                  ? `${SELECTION_BORDER_WIDTH}px dashed ${BORDER_COLOR}`
                  : undefined,
                borderTop: visibleSelectionRect.clipped.top
                  ? `${SELECTION_BORDER_WIDTH}px dashed ${BORDER_COLOR}`
                  : undefined,
                borderRight: visibleSelectionRect.clipped.right
                  ? `${SELECTION_BORDER_WIDTH}px dashed ${BORDER_COLOR}`
                  : undefined,
                borderBottom: visibleSelectionRect.clipped.bottom
                  ? `${SELECTION_BORDER_WIDTH}px dashed ${BORDER_COLOR}`
                  : undefined,
                boxSizing: "border-box",
                pointerEvents: "none",
              }}
            />
          )}

          {isCropMode && isFillImage && imageUrl && height !== undefined && (
            <CropGhostImage
              imageUrl={imageUrl}
              frameWidth={width}
              frameHeight={height}
              objectPosition={(overlay as ImageOverlay).objectPosition}
            />
          )}

          {/* Discoverability hint: double-click a fill image to reposition it.
              Shown only while selected, not already repositioning, and when the
              image actually overflows its frame (i.e. there's something to pan). */}
          {canAdjustImage && !isCropMode && !isCropDragging && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: "50%",
                marginTop: 6,
                transform: `translateX(-50%)${rotation ? ` rotate(${-rotation}deg)` : ""}`,
                transformOrigin: "top center",
                whiteSpace: "nowrap",
                pointerEvents: "none",
                background: "rgba(17,24,39,0.85)",
                color: "#fff",
                fontSize: 10.5,
                lineHeight: 1,
                padding: "4px 7px",
                borderRadius: 5,
                zIndex: 9999,
              }}
            >
              Double-click to adjust
            </div>
          )}

          {/* Rotation handle with connecting line */}
          <RotationHandleComponent
            onPointerDown={(e) => {
              e.stopPropagation();
              onRotationStart(e, overlay, elementRef);
            }}
          />

          {/* Corner handles - for all overlays */}
          <ResizeHandleComponent position="top-left" onResizeStart={handleResizeStart} />
          <ResizeHandleComponent position="top-right" onResizeStart={handleResizeStart} />
          <ResizeHandleComponent position="bottom-left" onResizeStart={handleResizeStart} />
          <ResizeHandleComponent position="bottom-right" onResizeStart={handleResizeStart} />

          {/* Side handles (left/right) - for all overlays */}
          <ResizeHandleComponent position="left" onResizeStart={handleResizeStart} />
          <ResizeHandleComponent position="right" onResizeStart={handleResizeStart} />

          {/* Top/bottom handles - only for non-text overlays */}
          {!isTextOverlay(overlay) && (
            <>
              <ResizeHandleComponent position="top" onResizeStart={handleResizeStart} />
              <ResizeHandleComponent position="bottom" onResizeStart={handleResizeStart} />
            </>
          )}

          {/* Floating toolbar */}
          {(isFillImage || onDuplicate || onDelete) && elementRef.current?.parentElement && createPortal(
            <div
              role="toolbar"
              aria-label="Overlay actions"
              aria-orientation="vertical"
              style={{
                position: "absolute",
                left: toolbarPosition.left,
                top: toolbarPosition.top,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                padding: "2px",
                background: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(4px)",
                borderRadius: 6,
                border: "1px solid rgba(0,0,0,0.1)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                zIndex: 10000,
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              {isFillImage && (
                <button
                  type="button"
                  aria-label={isCropMode ? "Done repositioning" : "Reposition image"}
                  style={{
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    background: isCropMode ? "rgba(255, 92, 53, 0.14)" : "transparent",
                    borderRadius: 4,
                    cursor: "pointer",
                    color: isCropMode ? "#FF5C35" : "#374151",
                  }}
                  onMouseEnter={(e) => {
                    if (!isCropMode) e.currentTarget.style.background = "rgba(0,0,0,0.06)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isCropMode
                      ? "rgba(255, 92, 53, 0.14)"
                      : "transparent";
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCropModeChange(isCropMode ? null : overlay.id);
                  }}
                  title={isCropMode ? "Done repositioning" : "Reposition image"}
                >
                  {isCropMode ? <Check size={14} /> : <Crop size={14} />}
                </button>
              )}
              {onDuplicate && (
                <button
                  type="button"
                  aria-label="Duplicate"
                  style={{
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    background: "transparent",
                    borderRadius: 4,
                    cursor: "pointer",
                    color: "#374151",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.06)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate(overlay.id);
                  }}
                  title="Duplicate"
                >
                  <Copy size={14} />
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  aria-label="Delete"
                  style={{
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    background: "transparent",
                    borderRadius: 4,
                    cursor: "pointer",
                    color: "#374151",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.06)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(overlay.id);
                  }}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>,
            elementRef.current.parentElement
          )}
        </>
      )}
    </div>
  );
};

const CropGhostImage: React.FC<{
  imageUrl: string;
  frameWidth: number;
  frameHeight: number;
  objectPosition?: { x: number; y: number };
}> = ({ imageUrl, frameWidth, frameHeight, objectPosition }) => {
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);

  const posX = objectPosition?.x ?? 50;
  const posY = objectPosition?.y ?? 50;
  const naturalWidth = naturalSize?.width ?? frameWidth;
  const naturalHeight = naturalSize?.height ?? frameHeight;
  const scale = Math.max(frameWidth / naturalWidth, frameHeight / naturalHeight);
  const renderedWidth = naturalWidth * scale;
  const renderedHeight = naturalHeight * scale;
  const left = -(renderedWidth - frameWidth) * (posX / 100);
  const top = -(renderedHeight - frameHeight) * (posY / 100);

  return (
    <img
      src={imageUrl}
      alt=""
      aria-hidden="true"
      draggable={false}
      onLoad={(event) => {
        const img = event.currentTarget;
        if (img.naturalWidth && img.naturalHeight) {
          setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
        }
      }}
      style={{
        position: "absolute",
        left,
        top,
        width: renderedWidth,
        height: renderedHeight,
        opacity: 0.24,
        pointerEvents: "none",
        userSelect: "none",
        maxWidth: "none",
        zIndex: 0,
      }}
    />
  );
};

// Renders invisible content to determine natural height
// Must match RemotionTextOverlay styling exactly
const InvisibleContent: React.FC<{ overlay: AnyOverlay; scale: number }> = ({ overlay, scale }) => {
  if (isTextOverlay(overlay)) {
    const { style, text } = overlay;
    const scaledFontSize = style.fontSize * scale;
    const isCJK = /[\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\u3040-\u309f\u30a0-\u30ff\uac00-\ud7af]/.test(text);
    const wordBreak: React.CSSProperties["wordBreak"] = isCJK ? "break-all" : "normal";

    // Check if using per-line background mode (useSvgFilter)
    const useSvgFilter = style.useSvgFilter;
    const hasBackground = !!(style.backgroundColor && style.opacity > 0);

    // Line height logic matching RemotionTextOverlay
    let lineHeight: number;
    if (useSvgFilter) {
      // Per-line mode: default to 1.4 to avoid overlap; respect user value if set
      lineHeight = style.lineHeight ?? 1.4;
    } else {
      // Regular mode
      lineHeight = style.lineHeight ?? 1.4;
    }

    // Padding logic matching RemotionTextOverlay
    let paddingStyle: string;
    if (useSvgFilter) {
      // Per-line mode uses different padding
      const scaledVertPadding = SVG_FILTER_VERT_PADDING * scale;
      const scaledHorizPadding = SVG_FILTER_HORIZ_PADDING * scale;
      paddingStyle = hasBackground
        ? `${scaledVertPadding}px ${scaledHorizPadding}px`
        : "0";
    } else {
      // Regular mode - only apply padding if has background
      if (hasBackground) {
        const paddingTop = (style.padding?.top ?? DEFAULT_VERTICAL_PADDING) * scale;
        const paddingRight = (style.padding?.right ?? DEFAULT_HORIZONTAL_PADDING) * scale;
        const paddingBottom = (style.padding?.bottom ?? DEFAULT_VERTICAL_PADDING) * scale;
        const paddingLeft = (style.padding?.left ?? DEFAULT_HORIZONTAL_PADDING) * scale;
        paddingStyle = `${paddingTop}px ${paddingRight}px ${paddingBottom}px ${paddingLeft}px`;
      } else {
        paddingStyle = "0";
      }
    }

    // For per-line mode with paragraphs, need to match paragraph structure
    if (useSvgFilter) {
      const paragraphs = text.split(/\n\n+/);
      const paragraphGap = scaledFontSize * lineHeight;

      return (
        <div
          style={{
            visibility: "hidden",
            fontSize: scaledFontSize,
            lineHeight,
          }}
        >
          {paragraphs.map((paragraph, index) => (
            <div
              key={index}
              style={{
                marginTop: index > 0 ? paragraphGap : 0,
                lineHeight,
              }}
            >
              <span
                style={{
                  display: "inline",
                  fontFamily: style.fontFamily,
                  fontSize: scaledFontSize,
                  fontWeight: style.fontWeight,
                  fontStyle: style.fontStyle,
                  lineHeight,
                  padding: paddingStyle,
                  boxDecorationBreak: "clone",
                  WebkitBoxDecorationBreak: "clone",
                  whiteSpace: "pre-wrap",
                  wordBreak,
                  overflowWrap: "normal",
                  hyphens: "none",
                }}
              >
                {paragraph}
              </span>
            </div>
          ))}
        </div>
      );
    }

    // Regular mode
    return (
      <div
        style={{
          visibility: "hidden",
          padding: paddingStyle,
          boxSizing: "border-box",
        }}
      >
        <span
          style={{
            display: "block",
            fontFamily: style.fontFamily,
            fontSize: scaledFontSize,
            fontWeight: style.fontWeight,
            fontStyle: style.fontStyle,
            lineHeight,
            whiteSpace: "pre-wrap",
            wordBreak,
            overflowWrap: "normal",
            hyphens: "none",
          }}
        >
          {text}
        </span>
      </div>
    );
  }

  // For SVGs without height, use viewBox aspect ratio; for images, default to square
  if (isSvgOverlay(overlay)) {
    const parts = overlay.viewBox.trim().split(/\s+/).map(Number);
    const vbW = parts[2] || 100;
    const vbH = parts[3] || 100;
    const aspectPct = (vbH / vbW) * 100;
    return <div style={{ paddingBottom: `${aspectPct}%` }} />;
  }
  return <div style={{ paddingBottom: "100%" }} />;
};

// Rotation handle component - circle above the selection box with connecting line
const RotationHandleComponent: React.FC<{ onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void }> = React.memo(({ onPointerDown }) => {
  const handleOffset = -(HANDLE_SIZE / 2);

  return (
    <div
      style={{
        position: "absolute",
        top: -(ROTATION_LINE_LENGTH + HANDLE_SIZE),
        left: "50%",
        marginLeft: handleOffset,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        zIndex: 10,
      }}
    >
      {/* Circular rotation handle */}
      <div
        style={{
          width: HANDLE_SIZE,
          height: HANDLE_SIZE,
          borderRadius: "50%",
          backgroundColor: HANDLE_FILL_COLOR,
          border: `1px solid ${HANDLE_STROKE_COLOR}`,
          cursor: "crosshair",
        }}
        onPointerDown={onPointerDown}
      />
      {/* Connecting line */}
      <div
        style={{
          width: 1,
          height: ROTATION_LINE_LENGTH,
          backgroundColor: BORDER_COLOR,
        }}
      />
    </div>
  );
});

// Resize handle component - Fabric.js style (circle, white fill, orange stroke)
interface ResizeHandleComponentProps {
  position: ResizeHandle;
  onResizeStart: (e: React.PointerEvent<HTMLDivElement>, handle: ResizeHandle) => void;
}

const ResizeHandleComponent: React.FC<ResizeHandleComponentProps> = React.memo(({
  position,
  onResizeStart,
}) => {
  const offset = -(HANDLE_SIZE / 2);

  const getPositionStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      position: "absolute",
      width: HANDLE_SIZE,
      height: HANDLE_SIZE,
      backgroundColor: HANDLE_FILL_COLOR,
      border: `1px solid ${HANDLE_STROKE_COLOR}`,
      borderRadius: "50%",
      zIndex: 10,
    };

    switch (position) {
      case "top-left":
        return { ...base, top: offset, left: offset, cursor: "nwse-resize" };
      case "top":
        return { ...base, top: offset, left: "50%", marginLeft: offset, cursor: "ns-resize" };
      case "top-right":
        return { ...base, top: offset, right: offset, cursor: "nesw-resize" };
      case "left":
        return { ...base, top: "50%", left: offset, marginTop: offset, cursor: "ew-resize" };
      case "right":
        return { ...base, top: "50%", right: offset, marginTop: offset, cursor: "ew-resize" };
      case "bottom-left":
        return { ...base, bottom: offset, left: offset, cursor: "nesw-resize" };
      case "bottom":
        return { ...base, bottom: offset, left: "50%", marginLeft: offset, cursor: "ns-resize" };
      case "bottom-right":
        return { ...base, bottom: offset, right: offset, cursor: "nwse-resize" };
    }
  };

  return (
    <div
      style={getPositionStyle()}
      onPointerDown={(e) => {
        e.stopPropagation();
        onResizeStart(e, position);
      }}
    />
  );
});

export default EditableOverlayLayer;
